import { useEffect, useMemo, useRef, useState } from "react";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import type { NotificationItem } from "@components/NotificationsDropdown/NotificationsDropdown.types";
import SideDrawer from "@components/SideDrawer/SideDrawer";
import type { DrawerType } from "@components/SideDrawer/SideDrawer.types.ts";
import Subheader from "@components/Subheader/Subheader";
import type { FilterFieldDefinition } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useDownloadPortServiceDocument } from "@features/vessel-call/vessel-call-status/PortServices/hooks/useDownloadPortServiceDocument.tsx";
import PageLayout from "@layouts/PageLayout";
import type { TableRow } from "@pages/notifications/NotificationsPage.types.ts";
import { isSystemAttributedBayHireNotification, mapApiToUi } from "@services/notifications/mapper";
import { buildNotificationDetailsPath } from "@services/notifications/routeHelpers";
import { fetchTerminalLocations } from "@services/notifications/terminalNameResolver";
import type { NotificationItemApi } from "@services/notifications/types";
import {
  getNotifications,
  getUnseenNotifications,
  markNotificationsSeen,
} from "@services/notificationsApi";
import type { PortService } from "@services/port-services/types";
import { getPortServicesByIds } from "@services/portServicesApi";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { normalizeRole } from "@utils/roles";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const LOCAL_STORAGE_PAGE_KEY = "notifications-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "notifications-page-size";
const isVesselCallClosedNotification = (type?: string) => type === "VESSEL_CALL_CLOSED";

const MARK_NOTIFICATIONS_SEEN_TRANSACTION_CONTEXT = {
  module: "notifications",
  screen: "Notifications",
  action: "mark_notifications_seen",
  entityType: "notification",
};

const NotificationsPage = () => {
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { t } = useTranslation();
  const isCustomsNotification = (type?: string) => type?.toUpperCase().includes("CUSTOMS");
  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : 25;
  });
  const [externalNote, setExternalNotes] = useState<string | null>(null);
  const [internalNotes, setInternalNotes] = useState<string | null>(null);
  const [internalAttachments, setInternalAttachments] = useState<string | null>(null);
  const [externalAttachments, setExternalAttachments] = useState<string | null>(null);
  const [cancelAttachment, setCancelAttachment] = useState<{
    documentName: string;
    documentUrl: string;
  } | null>(null);
  const [drawerType, setDrawerType] = useState<DrawerType | null>(null);
  const [rowTitle, setRowTitle] = useState<string>("");
  const handleActionClick = () => {
    setDrawerType(null);
  };

  const handleSubmit = () => {
    setDrawerType(null);
  };
  const headers: Header[] = [
    { field: "status", columnName: t("common.status"), percent: 15, renderAsChip: true },
    { field: "title", columnName: t("common.title"), percent: 45 },
    {
      field: "submittedAt",
      columnName: t("common.notification-date"),
      type: "date",
      percent: 20,
      dateFormat: "dd/MM/yyyy HH:mm",
    },
    { field: "submittedBy", columnName: t("common.submitted-by"), percent: 20 },
  ];
  // Tabs state: all | read | unread
  const [activeTab, setActiveTab] = useState<string>(() => {
    const saved = localStorage.getItem("notifications-active-tab");
    return saved ?? "all";
  });

  // Persist tab selection
  useEffect(() => {
    localStorage.setItem("notifications-active-tab", activeTab);
  }, [activeTab]);
  const AVAILABLE_FIELDS_EMPTY: Record<string, FilterFieldDefinition> = {};
  const { data, error, isLoading, refetch } = useQuery<{ rows: TableRow[]; total: number }>({
    queryKey: ["notifications.list", page, activeTab, pageSize],
    queryFn: async () => {
      const baseParams = { service: "bff-notification", page: page - 1, size: pageSize };
      const [res, terminalLocations] = await Promise.all([
        activeTab === "unread" ? getUnseenNotifications(baseParams) : getNotifications(baseParams),
        fetchTerminalLocations("bff-vessel-call").catch(() => []), // Fallback to empty array on error
      ]);

      const records = res.records ?? [];

      const portServiceIds = Array.from(
        new Set(
          records
            .filter((n) => n.relatedEntityType === "PORT_SERVICES" && !!n.relatedEntityId)
            .map((n) => n.relatedEntityId as string),
        ),
      );

      const portServices = await getPortServicesByIds({
        service: "bff-vessel-call",
        ids: portServiceIds,
        page: 0,
        pageSize: 50,
      }).catch(() => []);
      const portServicesById = new Map(portServices.map((service) => [service.id, service]));

      const rowsUnsorted = await Promise.all(
        records.map(async (n: NotificationItemApi) => {
          const entityData: PortService | null =
            n.relatedEntityType === "PORT_SERVICES" && n.relatedEntityId
              ? (portServicesById.get(n.relatedEntityId) ?? null)
              : null;

          const ui: NotificationItem = mapApiToUi(n, terminalLocations, entityData);
          const submittedAt: string = ui.submittedAt ?? ui.date ?? "";
          const nav =
            buildNotificationDetailsPath(
              n.relatedEntityType,
              n.relatedEntityId,
              n.secondRelatedEntityId,
            ) ?? "/notifications";

          return {
            id: ui.id,
            status: ui.status,
            title: ui.title,
            submittedBy: isSystemAttributedBayHireNotification(ui.type)
              ? ui.submittedBy
              : isVesselCallClosedNotification(ui.type) && ui.submittedByRole
                ? `${t(`roles.${normalizeRole(ui.submittedByRole)}`, {
                    defaultValue: ui.submittedByRole,
                  })}, ${ui.submittedBy}`
                : isCustomsNotification(ui.type) && n.submittedByRole
                  ? t(`roles.${normalizeRole(n.submittedByRole)}`, {
                      defaultValue: n.submittedByRole,
                    })
                  : ui.submittedBy === "SYSTEM" && ui.submittedByRole
                    ? t(`roles.${normalizeRole(ui.submittedByRole)}`, {
                        defaultValue: ui.submittedByRole,
                      })
                    : ui.submittedBy,
            submittedAt,
            externalNote: ui.externalNote ?? undefined,
            internalNotes: ui.internalNotes ?? undefined,
            internalAttachments: ui.internalAttachments ?? undefined,
            externalAttachments: ui.externalAttachments ?? undefined,
            restrictionName: ui.restrictionName ?? undefined,
            cancelAttachment: ui.cancelAttachement ?? undefined,
            seen: ui.seen,
            __nav: nav,
          } satisfies TableRow;
        }),
      );

      const rows: TableRow[] = rowsUnsorted.sort((a, b) => {
        const toTime = (s?: string) => (s ? new Date(s).getTime() : 0);
        return toTime(b.submittedAt) - toTime(a.submittedAt);
      });
      return { rows, total: res.totalRecordCount ?? rows.length };
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const location = useLocation();

  useEffect(() => {
    if (isLoading || error || hasTrackedInitialLoad.current) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    completeScreenNavigation("Notifications");
    trackScreenLoad("Notifications", "Notifications", startedAt.current);
  }, [error, isLoading]);

  // TO DO: This chunk has to be refactored. Temporary solution for cancel attachment
  const { handleDownload: downloadDocument, handlePreview: previewDocument } =
    useDownloadPortServiceDocument();
  const isValidUrl = (url: string | undefined): boolean => {
    if (!url) return false;
    return /^https?:\/\//.test(url);
  };
  const handleDownload = (documentUrl: string | undefined, documentName: string) => {
    if (documentUrl && isValidUrl(documentUrl)) {
      downloadDocument("bff-vessel-call", documentUrl, documentName);
    } else {
      console.error("Document not found, URL missing, or invalid URL:", documentUrl);
    }
  };

  const handlePreview = (documentUrl: string | undefined) => {
    if (documentUrl && isValidUrl(documentUrl)) {
      previewDocument("bff-vessel-call", documentUrl);
    } else {
      console.error("Document not found, URL missing, or invalid URL:", documentUrl);
    }
  };

  useEffect(() => {
    refetch();
  }, [location.key, activeTab]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const totalCount = data?.total ?? 0;
  return (
    <PageLayout dir={dir}>
      <Subheader title="Notifications" subtitle="Notifications" />
      <div className="px-24 mt-4 flex justify-end">
        <Tabs
          value={activeTab}
          onChange={(_, v) => {
            setActiveTab(v);
            setPage(1); // ensure pagination resets when switching tabs
          }}
          aria-label="notifications tabs"
          variant="scrollable"
        >
          <Tab
            value="all"
            label={t("common.all")}
            data-testid="notifications-tab-all"
            sx={{ textTransform: "none" }}
          />
          <Tab
            value="unread"
            label={t("common.unread")}
            data-testid="notifications-tab-unread"
            sx={{ textTransform: "none" }}
          />
        </Tabs>
      </div>

      <div className="mt-8 px-24">
        <CustomDataTable<TableRow>
          isNotificationTable={true}
          title={t("common.notifications").toLowerCase()}
          displaySubTitle={false}
          actionName=""
          testId="notifications-table"
          headers={headers}
          rows={rows}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          actionColumn={{
            actionTitle: t("common.view-details"),
            onActionClick: async (row) => {
              handleActionClick();
              setRowTitle(row.title);
              setExternalNotes(row.externalNote ?? null);
              setInternalNotes(row.internalNotes ?? null);
              setExternalAttachments(row.externalAttachments ?? null);
              setCancelAttachment(row.cancelAttachment ?? null);
              setInternalAttachments(row.internalAttachments ?? null);
              setDrawerType("notifications");
              await runTransaction(
                {
                  ...MARK_NOTIFICATIONS_SEEN_TRANSACTION_CONTEXT,
                  entityId: String(row.id),
                },
                (transactionId) =>
                  markNotificationsSeen({
                    service: "bff-notification",
                    ids: [String(row.id)],
                    ...(transactionId ? { transactionId } : {}),
                  }),
              );
              refetch();
            },
          }}
          canPreview
          navigationLinkBase=""
          navigationParam="__nav"
          onPageChange={(newPage, newSize) => {
            setPage(newPage);
            setPageSize(newSize);
            localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
            localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
          }}
          filters={[]}
          onApplyFilters={() => {}}
          onClearFilters={() => {}}
          availableFields={AVAILABLE_FIELDS_EMPTY}
          shouldFreezeExtraColumn
          hideFilters
        />
      </div>
      {drawerType && (
        <SideDrawer
          type={drawerType!}
          isOpen={drawerType !== null}
          onClose={() => setDrawerType(null)}
          title={t("security-clearance.details")}
          description={rowTitle}
          externalNotesLabel={t("vessel-management.vessel-status-page.externalNotes")}
          externalNotes={externalNote}
          externalNotesDocument={externalAttachments}
          cancelAttachment={cancelAttachment}
          internalNotesLabel={t("vessel-management.vessel-status-page.internalNotes")}
          internalNotes={internalNotes}
          internalNotesDocument={internalAttachments}
          submitButtonLabel={t("common.ok")}
          onSubmit={handleSubmit}
          onDownload={
            cancelAttachment
              ? () => handleDownload(cancelAttachment.documentUrl, cancelAttachment.documentName)
              : () => {}
          }
          onPreview={(url) => {
            if (url) return handlePreview(url);
          }}
        />
      )}
    </PageLayout>
  );
};

export default NotificationsPage;
